import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { api } from "../api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ForgotPassword = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    phone: "",
    password: "",
    otp: "",
  });

  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(0);
  const [showPassword, setShowPassword] = useState(false);

  // OTP resend countdown
  useEffect(() => {
    if (timer <= 0) return;

    const interval = setInterval(() => {
      setTimer((t) => (t <= 1 ? 0 : t - 1));
    }, 1000);

    return () => clearInterval(interval);
  }, [timer]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // 📤 Send OTP
  const handleSendOTP = async () => {
    if (!/^09\d{8}$/.test(formData.phone)) {
      toast.error("Phone number must start with 09 and be 10 digits.");
      return;
    }

    try {
      const res = await api.post("/users/forgot-password", {
        phone: formData.phone,
      });

      if (res.data.success) {
        toast.success("OTP sent successfully!");
        setTimer(30);
      } else {
        toast.error(res.data.message || "Failed to send OTP.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "OTP sending failed.");
    }
  };

  // 🔐 Reset Password
  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (!formData.phone || !formData.password || !formData.otp) {
      toast.error("Please fill all required fields.");
      return;
    }

    setLoading(true);

    try {
      const res = await api.put("/api/users/reset-password", {
        phone: formData.phone,
        otp: formData.otp,
        newPassword: formData.password,
      });
      console.log("response : ", res);
      if (res.data.success) {
        toast.success("Password reset successful!");
        setTimeout(() => navigate("/login"), 1500);
      } else {
        toast.error(res.data.message || "Reset failed.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Reset failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-primary text-white flex justify-center items-center px-4 py-6">
      <div className="w-full max-w-md bg-white/20 backdrop-blur-xl rounded-xl shadow-lg p-6 sm:p-8">
        <h1 className="text-2xl sm:text-3xl font-semibold mb-3 text-center text-white font-serif">
          Forgot Password
        </h1>

        <p className="text-gray-300 mb-6 text-center text-sm sm:text-base font-serif">
          Remembered Password?
          <a
            href="/login"
            className="text-blue-400 hover:text-blue-500 underline ml-1"
          >
            Login here
          </a>
        </p>

        <form onSubmit={handleResetPassword} className="space-y-4 text-left">
          {/* Phone */}
          <div>
            <label className="text-gray-200 text-sm font-serif">
              Phone Number
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={(e) => {
                const v = e.target.value;
                if (/^\d{0,10}$/.test(v)) {
                  setFormData((p) => ({ ...p, phone: v }));
                }
              }}
              placeholder="Enter Phone Number"
              className="mt-1 w-full bg-primary border border-[#2c4e74] text-white rounded-md px-3 py-2 sm:py-3 focus:ring-2 focus:ring-blue-400 outline-none"
            />
          </div>

          {/* Password */}
          <div>
            <label className="text-gray-200 text-sm font-serif">
              New Password
            </label>
            <div className="relative flex items-center">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter New Password"
                className="mt-1 w-full bg-primary border border-[#2c4e74] text-white rounded-md px-3 py-2 sm:py-3 focus:ring-2 focus:ring-blue-400 outline-none"
              />
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill={showPassword ? "#000" : "#058f05ff"}
                className="w-[18px] h-[18px] absolute right-2 cursor-pointer"
                onClick={() => setShowPassword((s) => !s)}
                viewBox="0 0 128 128"
              >
                <path d="M64 16c-32 0-58.4 20.8-64 48 5.6 27.2 32 48 64 48s58.4-20.8 64-48c-5.6-27.2-32-48-64-48zm0 80c-17.6 0-32-14.4-32-32s14.4-32 32-32 32 14.4 32 32-14.4 32-32 32z" />
              </svg>
            </div>
          </div>

          {/* OTP */}
          <div className="flex gap-2">
            <input
              type="text"
              name="otp"
              value={formData.otp}
              onChange={handleChange}
              placeholder="OTP"
              className="flex-1 bg-primary border border-[#2c4e74] text-white rounded-md px-3 py-2 sm:py-3 focus:ring-2 focus:ring-blue-400 outline-none"
            />

            <button
              type="button"
              onClick={handleSendOTP}
              disabled={timer > 0}
              className={`px-4 py-2 sm:py-3 rounded-md ${
                timer > 0
                  ? "bg-red-500 cursor-not-allowed"
                  : "bg-gradient-to-br from-primary to-cyan-600"
              }`}
            >
              {timer > 0 ? `Resend in ${timer}s` : "Send OTP"}
            </button>
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-2 sm:py-3 rounded-md font-semibold ${
              loading
                ? "bg-gray-600 cursor-not-allowed"
                : "bg-gradient-to-r from-red-500 to-cyan-600"
            }`}
          >
            {loading ? "Resetting..." : "Reset Password"}
          </button>
        </form>
      </div>

      <ToastContainer theme="colored" autoClose={3000} />
    </div>
  );
};

export default ForgotPassword;
